import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import BatteryStatus from '../../../types/physical-sample/BatteryStatus';
import PhysicalSample from '../../../types/physical-sample/PhysicalSample';
import PhysicalSampleDiscovery from '../../../types/physical-sample/PhysicalSampleDiscovery';
import PhysicalSampleEligibilityDiagnostics from '../../../types/physical-sample/PhysicalSampleEligibilityDiagnostics';
import PhysicalSampleState from '../../../types/physical-sample/PhysicalSampleState';
import PhysicalSampleWriteDTO from '../../../types/physical-sample/PhysicalSampleWriteDTO';
import Page from '../../../types/shared/Page';
import { BaseService } from './base.service';

@Injectable({
    providedIn: 'root',
})
export class PhysicalSampleService {
    private RESOURCE: string = 'physical-sample';

    constructor(private baseService: BaseService) {
    }

    /**
     * Fetches the physical samples from the backend.
     *
     * @param search The search query to filter the physical samples by.
     * @param page The page number to fetch.
     * @param size The number of elements to fetch per page.
     * @param state Filter by physical sample states.
     * @param firmwareVersions Filter by firmware versions.
     * @param ownerDealershipIds Filter by dealership IDs.
     * @param studioIds Filter by studio IDs.
     * @param sampleIds Filter by sample IDs.
     * @param batteryStatuses Filter by battery statuses.
     * @param sortField Field to sort by.
     * @param sortDirection 1 for ASC, -1 for DESC.
     * @returns The physical samples.
     */
    public async getPhysicalSamples(
        search?: string,
        page: number = 0,
        size: number = 10,
        state?: PhysicalSampleState[],
        firmwareVersions?: string[],
        ownerDealershipIds?: number[],
        studioIds?: number[],
        sampleIds?: number[],
        batteryStatuses?: BatteryStatus[],
        sortField?: string,
        sortDirection?: number
    ): Promise<Page<PhysicalSample>> {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('size', size.toString());

        if (search) {
            params = params.set('search', search);
        }
        if (state && state.length > 0) {
            state.forEach(s => ( params = params.append('state', s) ));
        }
        if (firmwareVersions && firmwareVersions.length > 0) {
            firmwareVersions.forEach(f => ( params = params.append('firmwareVersion', f) ));
        }
        if (ownerDealershipIds && ownerDealershipIds.length > 0) {
            ownerDealershipIds.forEach(id => ( params = params.append('ownerDealershipId', id.toString()) ));
        }
        if (studioIds && studioIds.length > 0) {
            studioIds.forEach(id => ( params = params.append('studioId', id.toString()) ));
        }
        if (sampleIds && sampleIds.length > 0) {
            sampleIds.forEach(id => ( params = params.append('sampleId', id.toString()) ));
        }
        if (batteryStatuses && batteryStatuses.length > 0) {
            batteryStatuses.forEach(b => ( params = params.append('batteryStatus', b) ));
        }
        if (sortField) {
            params = params.set('sortField', sortField);
        }
        if (sortDirection !== undefined && sortDirection !== null) {
            params = params.set('sortDirection', sortDirection.toString());
        }

        return await this.baseService.get<Page<PhysicalSample>>(this.RESOURCE, params);
    }

    /**
     * Fetches all physical samples from the backend.
     *
     * @return The physical samples.
     */
    public async getAllPhysicalSamples(): Promise<PhysicalSample[]> {
        return await this.baseService.get<PhysicalSample[]>(this.RESOURCE + '/all');
    }

    /**
     * Fetches a physical sample from the backend.
     *
     * @param id The ID of the physical sample to fetch.
     * @return The physical sample.
     */
    public async getPhysicalSample(id: number): Promise<PhysicalSample> {
        return await this.baseService.get<PhysicalSample>(this.RESOURCE + '/' + id);
    }

    /**
     * Creates a physical sample in the backend.
     *
     * @param physicalSample The physical sample to create.
     * @return The response from the backend.
     */
    public async createPhysicalSample(physicalSample: PhysicalSampleWriteDTO): Promise<void> {
        return await this.baseService.post<void>(this.RESOURCE, physicalSample);
    }

    public async getPhysicalSampleDiscoveries(): Promise<PhysicalSampleDiscovery[]> {
        const discoveries = await this.baseService.get<any[]>('physical-sample-discovery');
        return discoveries.map(discovery => new PhysicalSampleDiscovery(discovery));
    }

    public async confirmPhysicalSampleDiscovery(
        discoveryId: number,
        sampleId: number,
        studioId?: number
    ): Promise<PhysicalSample> {
        return await this.baseService.post<PhysicalSample>(
            `physical-sample-discovery/${ discoveryId }/confirm`,
            { sampleId, studioId }
        );
    }

    public async ignorePhysicalSampleDiscovery(discoveryId: number): Promise<void> {
        return await this.baseService.post<void>(
            `physical-sample-discovery/${ discoveryId }/ignore`, {}
        );
    }

    /**
     * Updates a physical sample in the backend.
     *
     * @param physicalSample The physical sample to update.
     * @return The response from the backend.
     */
    public async updatePhysicalSample(physicalSample: PhysicalSampleWriteDTO): Promise<void> {
        return await this.baseService.put<void>(
            this.RESOURCE + '/' + physicalSample.id,
            physicalSample
        );
    }

    /**
     * Deletes a physical sample from the backend.
     *
     * @param id The ID of the physical sample to delete.
     * @return The response from the backend.
     */
    public async deletePhysicalSample(id: number): Promise<void> {
        return await this.baseService.delete<void>(this.RESOURCE + '/' + id);
    }

    /**
     * Samples in the studio that are hardware-compatible with the revision and not already on it.
     *
     * @param studioId The studio to search within.
     * @param firmwareRevisionId The target firmware revision.
     * @return The eligible physical samples.
     */
    public async getEligiblePhysicalSamples(studioId: number, firmwareRevisionId: number): Promise<PhysicalSample[]> {
        const params = new HttpParams()
            .set('studioId', studioId.toString())
            .set('firmwareRevisionId', firmwareRevisionId.toString());
        const samples = await this.baseService.get<any[]>(this.RESOURCE + '/eligible', params);
        return samples.map(sample => new PhysicalSample(sample));
    }

    /**
     * Distinguishes "nothing to update" (no samples / already updated) from a pcb mapping that
     * matches no compatible hardware revision.
     *
     * @param studioId The studio to search within.
     * @param firmwareRevisionId The target firmware revision.
     * @return Diagnostic counts/values for the studio.
     */
    public async getEligibilityDiagnostics(studioId: number,
                                           firmwareRevisionId: number): Promise<PhysicalSampleEligibilityDiagnostics> {
        const params = new HttpParams()
            .set('studioId', studioId.toString())
            .set('firmwareRevisionId', firmwareRevisionId.toString());
        const diagnostics = await this.baseService.get<any>(
            this.RESOURCE + '/eligibility-diagnostics', params);
        return new PhysicalSampleEligibilityDiagnostics(diagnostics);
    }

    /**
     * Fetches all distinct firmware versions from the backend.
     *
     * @return The distinct firmware versions.
     */
    public async getDistinctFirmwareVersions(): Promise<string[]> {
        return await this.baseService.get<string[]>(this.RESOURCE + '/firmware-versions');
    }
}
